/* Qorvex strategy pool — ready templates for Forge Classic.
 *
 * Modelled on the families in the public AlgoBulls strategy pool
 * (github.com/algobulls/pyalgostrategypool): EMA, MACD, RSI-MACD, reverse
 * RSI, Bollinger, mean-reversion Bollinger, VWAP, stochastic, Aroon, ATR
 * volatility trend and inverse EMA scalping, each in an intraday and a
 * delivery flavour. These are OUR OWN rule-graph renditions of those ideas,
 * not ports of that code: the engine's rule grammar is what runs here.
 *
 * Two engine facts shape every template:
 *
 *   1. The rule graph is LONG-ONLY. `entry` opens a long, `exit` closes it.
 *      Where the reference family also shorts on the mirror signal, the
 *      mirror signal is this template's exit instead. Shorting is a Python
 *      lane concern.
 *   2. Indicator periods are FIXED in the engine's bank: SMA 20, EMA 200,
 *      RSI 14, MACD 12/26/9, Bollinger 20/2, ATR 14, ADX 14, Stoch 14/3,
 *      Supertrend 10/3. A family that needs two EMAs (fast/slow) is rendered
 *      as EMA 200 against SMA 20 — the slow and fast lines the engine has.
 *      Aroon is not in the bank, so the Aroon family is rendered with the
 *      nearest available trend-strength construct (ADX + price structure)
 *      and says so.
 *
 * `unique: true` marks Qorvex's own strategies, ARM (intraday), COIL and
 * TIDE (delivery), which are shown first. Their rule-graph forms are deliberately
 * the SIMPLE renditions (the grammar has no time window, no percentile
 * regime, no lifecycle stop); the full versions are in
 * quant/qorvex_quant/strategies/{arm,coil,tide}.py and the Python lane templates.
 *
 * Nothing here is a performance claim. Every template is a starting point
 * that must be backtested, then paper traded, before it is ever deployed.
 */
import type { RuleGraph, RuleSet } from '@/types'

export type PoolCategory = 'Intraday' | 'Delivery'
export type PoolFamily = 'Trend' | 'Momentum' | 'Mean reversion' | 'Volatility'

export interface PoolTemplate {
  id: string
  name: string
  category: PoolCategory
  family: PoolFamily
  /** Indicator operands the graph uses, for the picker's column. */
  indicators: string[]
  /** One paragraph: what the idea is and how the rendition differs. */
  description: string
  /** Default parameter summary shown in the picker, in words. */
  defaults: string
  /** Which pyalgostrategypool family this mirrors. */
  source: string
  /** Qorvex's own strategy. */
  unique?: boolean
  entry: RuleSet
  exit: RuleSet
  risk: RuleGraph['risk']
}

const c = (left: RuleSet['conditions'][number]['left'],
           op: RuleSet['conditions'][number]['op'],
           rightOrValue: RuleSet['conditions'][number]['left'] | number)
  : RuleSet['conditions'][number] =>
  typeof rightOrValue === 'number'
    ? { left, op, value: rightOrValue }
    : { left, op, right: rightOrValue }

// Intraday: tighter stops, a take-profit that fits one session, a trailing
// stop. Delivery: wider stops, no trailing, room for the trend to breathe.
const INTRADAY_RISK: RuleGraph['risk'] =
  { position_size: 1, max_position: 1, stop_loss_pct: 1.0, take_profit_pct: 2.0, trailing_stop_pct: 0.75 }
const DELIVERY_RISK: RuleGraph['risk'] =
  { position_size: 1, max_position: 1, stop_loss_pct: 4.0, take_profit_pct: 10.0, trailing_stop_pct: 0 }

const POOL_URL = 'github.com/algobulls/pyalgostrategypool'

export const STRATEGY_POOL: PoolTemplate[] = [
  // -------------------------------------------------------------- unique
  {
    id: 'qorvex-arm-intraday',
    name: 'Qorvex ARM — Adaptive Regime Momentum',
    category: 'Intraday',
    family: 'Trend',
    unique: true,
    indicators: ['VWAP', 'EMA', 'ADX', 'ATR', 'PNL_PCT'],
    description:
      'Qorvex\'s own strategy. Trades intraday continuation in the direction ' +
      'the session has already chosen: price above the 200 EMA, trend strength ' +
      'confirmed by ADX, and a pullback that RECLAIMS VWAP rather than the first ' +
      'touch. This rule-graph form is the simple rendition the engine grammar ' +
      'allows (no time window, no ATR-percentile regime, no chandelier stop); ' +
      'the full ARM with those elements is the Python lane template and the ' +
      'quant module qorvex_quant.strategies.arm.',
    defaults:
      'Entry: close crosses above VWAP, close > EMA 200, ADX > 20. Exit: close ' +
      'crosses below VWAP, or unrealised P&L reaches +1.5%. Stop 0.6%, target ' +
      '1.5%, trailing 0.5%.',
    source: 'Qorvex original — not in the reference pool',
    entry: { combinator: 'AND', conditions: [
      c('CLOSE', 'CROSSES_ABOVE', 'VWAP'),
      c('CLOSE', '>', 'EMA'),
      c('ADX', '>', 20),
    ] },
    exit: { combinator: 'OR', conditions: [
      c('CLOSE', 'CROSSES_BELOW', 'VWAP'),
      c('PNL_PCT', '>=', 1.5),
    ] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 0.6, take_profit_pct: 1.5, trailing_stop_pct: 0.5 },
  },

  {
    id: 'qorvex-coil-delivery',
    name: 'Qorvex COIL — Compression Release Breakout',
    category: 'Delivery',
    family: 'Volatility',
    unique: true,
    indicators: ['BB_UPPER', 'BB_MIDDLE', 'VOLUME', 'AVG_VOLUME', 'EMA', 'PNL_PCT'],
    description:
      'Qorvex\'s own swing strategy, the positional complement of ARM. Waits ' +
      'for a Bollinger-bandwidth coil, then enters on the volume-confirmed bar ' +
      'that breaks the upper band with price above the long EMA, and runs a ' +
      'stop LIFECYCLE (ATR stop, breakeven if the release hesitates, chandelier ' +
      'trail after +1R, time stop). This rule-graph form is the simple ' +
      'rendition the engine grammar allows (no bandwidth percentile, no ' +
      'hysteresis, no lifecycle stop); the full COIL is the Python lane ' +
      'template and the quant module qorvex_quant.strategies.coil.',
    defaults:
      'Entry: close crosses above upper Bollinger band, volume > average ' +
      'volume, close > EMA 200. Exit: close crosses below the Bollinger middle ' +
      'band, or unrealised P&L reaches +12%. Stop 4%, target 12%, trailing 3%.',
    source: 'Qorvex original — not in the reference pool',
    entry: { combinator: 'AND', conditions: [
      c('CLOSE', 'CROSSES_ABOVE', 'BB_UPPER'),
      c('VOLUME', '>', 'AVG_VOLUME'),
      c('CLOSE', '>', 'EMA'),
    ] },
    exit: { combinator: 'OR', conditions: [
      c('CLOSE', 'CROSSES_BELOW', 'BB_MIDDLE'),
      c('PNL_PCT', '>=', 12),
    ] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 4.0, take_profit_pct: 12.0, trailing_stop_pct: 3.0 },
  },

  {
    id: 'qorvex-tide-delivery',
    name: 'Qorvex TIDE — Trend-Intact Dip Entry',
    category: 'Delivery',
    family: 'Mean reversion',
    unique: true,
    indicators: ['RSI', 'EMA', 'BB_LOWER', 'BB_MIDDLE', 'PNL_PCT'],
    description:
      'Qorvex\'s own mean-reversion strategy. In a stock whose long-term ' +
      'trend is up, a short run of down closes that leaves price stretched ' +
      'below its short average is bought on further weakness and sold at the ' +
      'first sign of recovery, with a short time stop and a wide disaster ' +
      'stop. This rule-graph form is the simple rendition the engine grammar ' +
      'allows (no ATR-stretch, no resting limit below the close, no ' +
      'cross-sectional ranking, no next-open exit); the full TIDE is the ' +
      'Python lane template and the quant module qorvex_quant.strategies.tide.',
    defaults:
      'Entry: RSI < 30, close < lower Bollinger band, close > EMA 200. Exit: ' +
      'close crosses above the Bollinger middle band, or unrealised P&L ' +
      'reaches +3%. Stop 6%, target 4%, no trailing.',
    source: 'Qorvex original — not in the reference pool',
    entry: { combinator: 'AND', conditions: [
      c('RSI', '<', 30),
      c('CLOSE', '<', 'BB_LOWER'),
      c('CLOSE', '>', 'EMA'),
    ] },
    exit: { combinator: 'OR', conditions: [
      c('CLOSE', 'CROSSES_ABOVE', 'BB_MIDDLE'),
      c('PNL_PCT', '>=', 3),
    ] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 6.0, take_profit_pct: 4.0, trailing_stop_pct: 0 },
  },

  // -------------------------------------------------------------- EMA
  {
    id: 'ema-crossover-intraday',
    name: 'EMA Crossover — Intraday',
    category: 'Intraday', family: 'Trend',
    indicators: ['SMA', 'EMA'],
    description:
      'The classic fast/slow moving-average crossover. Enter long when the ' +
      'fast line crosses above the slow line; exit when it crosses back below. ' +
      'The engine\'s bank has one EMA (200) and one SMA (20), so fast = SMA 20 ' +
      'and slow = EMA 200.',
    defaults: 'Entry: SMA 20 crosses above EMA 200. Exit: SMA 20 crosses below EMA 200.',
    source: `${POOL_URL} · strategy_ema_regular_order_intraday`,
    entry: { combinator: 'AND', conditions: [c('SMA', 'CROSSES_ABOVE', 'EMA')] },
    exit:  { combinator: 'OR',  conditions: [c('SMA', 'CROSSES_BELOW', 'EMA')] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'ema-crossover-delivery',
    name: 'EMA Crossover — Delivery',
    category: 'Delivery', family: 'Trend',
    indicators: ['SMA', 'EMA'],
    description:
      'The same crossover held across sessions. Wider stop and no trailing ' +
      'stop, so a position is not shaken out by one day\'s noise.',
    defaults: 'Entry: SMA 20 crosses above EMA 200. Exit: SMA 20 crosses below EMA 200. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_ema_regular_order_delivery`,
    entry: { combinator: 'AND', conditions: [c('SMA', 'CROSSES_ABOVE', 'EMA')] },
    exit:  { combinator: 'OR',  conditions: [c('SMA', 'CROSSES_BELOW', 'EMA')] },
    risk: DELIVERY_RISK,
  },
  {
    id: 'inverse-ema-scalping-intraday',
    name: 'Inverse EMA Scalping — Intraday',
    category: 'Intraday', family: 'Mean reversion',
    indicators: ['SMA', 'EMA'],
    description:
      'Fades the crossover: when the fast line crosses BELOW the slow line the ' +
      'move is treated as exhausted and a long is taken for the snap back, ' +
      'closed on the next crossover or a tight target. Scalping sizes: small ' +
      'stop, small target, trailing on.',
    defaults: 'Entry: SMA 20 crosses below EMA 200. Exit: SMA 20 crosses above EMA 200. Stop 0.5%, target 0.8%.',
    source: `${POOL_URL} · strategy_inverse_ema_scalping_regular_order_intraday`,
    entry: { combinator: 'AND', conditions: [c('SMA', 'CROSSES_BELOW', 'EMA')] },
    exit:  { combinator: 'OR',  conditions: [c('SMA', 'CROSSES_ABOVE', 'EMA')] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 0.5, take_profit_pct: 0.8, trailing_stop_pct: 0.3 },
  },
  {
    id: 'inverse-ema-scalping-delivery',
    name: 'Inverse EMA Scalping — Delivery',
    category: 'Delivery', family: 'Mean reversion',
    indicators: ['SMA', 'EMA'],
    description: 'The inverse crossover carried across sessions with delivery-sized risk.',
    defaults: 'Entry: SMA 20 crosses below EMA 200. Exit: SMA 20 crosses above EMA 200. Stop 3%, target 5%.',
    source: `${POOL_URL} · strategy_inverse_ema_scalping_regular_order_delivery`,
    entry: { combinator: 'AND', conditions: [c('SMA', 'CROSSES_BELOW', 'EMA')] },
    exit:  { combinator: 'OR',  conditions: [c('SMA', 'CROSSES_ABOVE', 'EMA')] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 3, take_profit_pct: 5, trailing_stop_pct: 0 },
  },

  // -------------------------------------------------------------- MACD
  {
    id: 'macd-crossover-intraday',
    name: 'MACD Crossover — Intraday',
    category: 'Intraday', family: 'Momentum',
    indicators: ['MACD', 'MACD_SIGNAL'],
    description:
      'Enter when the MACD line crosses above its signal line; exit when it ' +
      'crosses back below. The reference family shorts the mirror signal — ' +
      'here that is the exit.',
    defaults: 'Entry: MACD crosses above signal. Exit: MACD crosses below signal.',
    source: `${POOL_URL} · strategy_macd_crossover_intraday`,
    entry: { combinator: 'AND', conditions: [c('MACD', 'CROSSES_ABOVE', 'MACD_SIGNAL')] },
    exit:  { combinator: 'OR',  conditions: [c('MACD', 'CROSSES_BELOW', 'MACD_SIGNAL')] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'macd-crossover-delivery',
    name: 'MACD Crossover — Delivery',
    category: 'Delivery', family: 'Momentum',
    indicators: ['MACD', 'MACD_SIGNAL'],
    description: 'The MACD/signal crossover held across sessions.',
    defaults: 'Entry: MACD crosses above signal. Exit: MACD crosses below signal. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_macd_crossover_delivery`,
    entry: { combinator: 'AND', conditions: [c('MACD', 'CROSSES_ABOVE', 'MACD_SIGNAL')] },
    exit:  { combinator: 'OR',  conditions: [c('MACD', 'CROSSES_BELOW', 'MACD_SIGNAL')] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- RSI + MACD
  {
    id: 'rsi-macd-crossover-intraday',
    name: 'RSI + MACD Crossover — Intraday',
    category: 'Intraday', family: 'Momentum',
    indicators: ['RSI', 'MACD', 'MACD_SIGNAL'],
    description:
      'Momentum with a filter: a long needs the MACD crossover AND an RSI that ' +
      'has left oversold territory (crossed above 30). Exit on the MACD cross ' +
      'back below or RSI reaching overbought.',
    defaults: 'Entry: MACD crosses above signal AND RSI > 30. Exit: MACD crosses below signal OR RSI >= 70.',
    source: `${POOL_URL} · strategy_rsi_macd_crossover_intraday`,
    entry: { combinator: 'AND', conditions: [
      c('MACD', 'CROSSES_ABOVE', 'MACD_SIGNAL'), c('RSI', '>', 30),
    ] },
    exit: { combinator: 'OR', conditions: [
      c('MACD', 'CROSSES_BELOW', 'MACD_SIGNAL'), c('RSI', '>=', 70),
    ] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'rsi-macd-crossover-delivery',
    name: 'RSI + MACD Crossover — Delivery',
    category: 'Delivery', family: 'Momentum',
    indicators: ['RSI', 'MACD', 'MACD_SIGNAL'],
    description: 'The filtered momentum entry held across sessions.',
    defaults: 'Entry: MACD crosses above signal AND RSI > 30. Exit: MACD crosses below signal OR RSI >= 70. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_rsi_macd_crossover_delivery`,
    entry: { combinator: 'AND', conditions: [
      c('MACD', 'CROSSES_ABOVE', 'MACD_SIGNAL'), c('RSI', '>', 30),
    ] },
    exit: { combinator: 'OR', conditions: [
      c('MACD', 'CROSSES_BELOW', 'MACD_SIGNAL'), c('RSI', '>=', 70),
    ] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- reverse RSI
  {
    id: 'reverse-rsi-crossover-intraday',
    name: 'Reverse RSI Crossover — Intraday',
    category: 'Intraday', family: 'Mean reversion',
    indicators: ['RSI'],
    description:
      'Buys the recovery out of oversold: enter when RSI crosses above 30, ' +
      'exit when it crosses below 70 from above (momentum fading) or the ' +
      'stop is hit.',
    defaults: 'Entry: RSI crosses above 30. Exit: RSI crosses below 70.',
    source: `${POOL_URL} · strategy_reverse_rsi_crossover_intraday`,
    entry: { combinator: 'AND', conditions: [c('RSI', 'CROSSES_ABOVE', 30)] },
    exit:  { combinator: 'OR',  conditions: [c('RSI', 'CROSSES_BELOW', 70)] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'reverse-rsi-crossover-delivery',
    name: 'Reverse RSI Crossover — Delivery',
    category: 'Delivery', family: 'Mean reversion',
    indicators: ['RSI'],
    description: 'The oversold-recovery entry held across sessions.',
    defaults: 'Entry: RSI crosses above 30. Exit: RSI crosses below 70. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_reverse_rsi_crossover_delivery`,
    entry: { combinator: 'AND', conditions: [c('RSI', 'CROSSES_ABOVE', 30)] },
    exit:  { combinator: 'OR',  conditions: [c('RSI', 'CROSSES_BELOW', 70)] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- Bollinger
  {
    id: 'bollinger-bands-intraday',
    name: 'Bollinger Bands — Intraday',
    category: 'Intraday', family: 'Volatility',
    indicators: ['BB_LOWER', 'BB_MIDDLE', 'BB_UPPER'],
    description:
      'The rebound-from-the-lower-band idea: enter when close crosses back ' +
      'above the lower band after touching it; exit at the middle band cross ' +
      'down or the upper band.',
    defaults: 'Entry: close crosses above BB lower. Exit: close >= BB upper OR close crosses below BB middle.',
    source: `${POOL_URL} · strategy_bollinger_bands_intraday`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'BB_LOWER')] },
    exit: { combinator: 'OR', conditions: [
      c('CLOSE', '>=', 'BB_UPPER'), c('CLOSE', 'CROSSES_BELOW', 'BB_MIDDLE'),
    ] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'bollinger-bands-delivery',
    name: 'Bollinger Bands — Delivery',
    category: 'Delivery', family: 'Volatility',
    indicators: ['BB_LOWER', 'BB_MIDDLE', 'BB_UPPER'],
    description: 'The lower-band rebound held across sessions.',
    defaults: 'Entry: close crosses above BB lower. Exit: close >= BB upper OR close crosses below BB middle. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_bollinger_bands_delivery`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'BB_LOWER')] },
    exit: { combinator: 'OR', conditions: [
      c('CLOSE', '>=', 'BB_UPPER'), c('CLOSE', 'CROSSES_BELOW', 'BB_MIDDLE'),
    ] },
    risk: DELIVERY_RISK,
  },
  {
    id: 'mean-reversion-bollinger-intraday',
    name: 'Mean Reversion Bollinger — Intraday',
    category: 'Intraday', family: 'Mean reversion',
    indicators: ['BB_LOWER', 'BB_MIDDLE', 'RSI'],
    description:
      'Buys a close BELOW the lower band (a stretched move) with RSI confirming ' +
      'oversold, and exits at the middle band — the mean the trade is ' +
      'reverting to.',
    defaults: 'Entry: close < BB lower AND RSI < 35. Exit: close >= BB middle.',
    source: `${POOL_URL} · strategy_mean_reversion_bollinger_bands_intraday`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', '<', 'BB_LOWER'), c('RSI', '<', 35)] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', '>=', 'BB_MIDDLE')] },
    risk: { position_size: 1, max_position: 1, stop_loss_pct: 1.2, take_profit_pct: 2.0, trailing_stop_pct: 0 },
  },
  {
    id: 'mean-reversion-bollinger-delivery',
    name: 'Mean Reversion Bollinger — Delivery',
    category: 'Delivery', family: 'Mean reversion',
    indicators: ['BB_LOWER', 'BB_MIDDLE', 'RSI'],
    description: 'The stretched-move reversion held across sessions.',
    defaults: 'Entry: close < BB lower AND RSI < 35. Exit: close >= BB middle. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_mean_reversion_bollinger_bands_delivery`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', '<', 'BB_LOWER'), c('RSI', '<', 35)] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', '>=', 'BB_MIDDLE')] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- VWAP
  {
    id: 'vwap-crossover-intraday',
    name: 'VWAP Crossover — Intraday',
    category: 'Intraday', family: 'Trend',
    indicators: ['VWAP'],
    description:
      'Enter when close crosses above the session VWAP; exit when it crosses ' +
      'back below. VWAP resets every session in the engine, which is what ' +
      'makes this an intraday idea.',
    defaults: 'Entry: close crosses above VWAP. Exit: close crosses below VWAP.',
    source: `${POOL_URL} · strategy_vwap_crossover_intraday`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'VWAP')] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', 'CROSSES_BELOW', 'VWAP')] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'vwap-crossover-delivery',
    name: 'VWAP Crossover — Delivery',
    category: 'Delivery', family: 'Trend',
    indicators: ['VWAP', 'EMA'],
    description:
      'The reference pool carries a delivery VWAP variant; because VWAP resets ' +
      'per session, this rendition adds the EMA 200 as the multi-day filter so ' +
      'the held position has a trend behind it.',
    defaults: 'Entry: close crosses above VWAP AND close > EMA 200. Exit: close < EMA 200. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_vwap_crossover_delivery`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'VWAP'), c('CLOSE', '>', 'EMA')] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', '<', 'EMA')] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- Stochastic
  {
    id: 'stochastic-crossover-intraday',
    name: 'Stochastic Crossover — Intraday',
    category: 'Intraday', family: 'Momentum',
    indicators: ['STOCH_K', 'STOCH_D'],
    description:
      'Enter when %K crosses above %D from a low reading (below 30); exit when ' +
      '%K crosses back below %D or the oscillator reaches 80.',
    defaults: 'Entry: %K crosses above %D AND %D < 30. Exit: %K crosses below %D OR %K >= 80.',
    source: `${POOL_URL} · strategy_stochastic_crossover_intraday`,
    entry: { combinator: 'AND', conditions: [c('STOCH_K', 'CROSSES_ABOVE', 'STOCH_D'), c('STOCH_D', '<', 30)] },
    exit:  { combinator: 'OR',  conditions: [c('STOCH_K', 'CROSSES_BELOW', 'STOCH_D'), c('STOCH_K', '>=', 80)] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'stochastic-crossover-delivery',
    name: 'Stochastic Crossover — Delivery',
    category: 'Delivery', family: 'Momentum',
    indicators: ['STOCH_K', 'STOCH_D'],
    description: 'The oscillator crossover held across sessions.',
    defaults: 'Entry: %K crosses above %D AND %D < 30. Exit: %K crosses below %D OR %K >= 80. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_stochastic_crossover_delivery`,
    entry: { combinator: 'AND', conditions: [c('STOCH_K', 'CROSSES_ABOVE', 'STOCH_D'), c('STOCH_D', '<', 30)] },
    exit:  { combinator: 'OR',  conditions: [c('STOCH_K', 'CROSSES_BELOW', 'STOCH_D'), c('STOCH_K', '>=', 80)] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- Aroon
  {
    id: 'aroon-crossover-intraday',
    name: 'Aroon-style Trend Crossover — Intraday',
    category: 'Intraday', family: 'Trend',
    indicators: ['SUPERTREND', 'ADX'],
    description:
      'Aroon is not in the engine\'s indicator bank. The family\'s intent — ' +
      'a new-high regime beating a new-low regime — is rendered with the ' +
      'Supertrend flip as the direction signal and ADX as the strength gate.',
    defaults: 'Entry: close crosses above Supertrend AND ADX > 20. Exit: close crosses below Supertrend.',
    source: `${POOL_URL} · strategy_aroon_crossover_intraday (rendered with Supertrend + ADX)`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'SUPERTREND'), c('ADX', '>', 20)] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', 'CROSSES_BELOW', 'SUPERTREND')] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'aroon-crossover-delivery',
    name: 'Aroon-style Trend Crossover — Delivery',
    category: 'Delivery', family: 'Trend',
    indicators: ['SUPERTREND', 'ADX'],
    description: 'The Supertrend + ADX rendition held across sessions.',
    defaults: 'Entry: close crosses above Supertrend AND ADX > 20. Exit: close crosses below Supertrend. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_aroon_crossover_delivery (rendered with Supertrend + ADX)`,
    entry: { combinator: 'AND', conditions: [c('CLOSE', 'CROSSES_ABOVE', 'SUPERTREND'), c('ADX', '>', 20)] },
    exit:  { combinator: 'OR',  conditions: [c('CLOSE', 'CROSSES_BELOW', 'SUPERTREND')] },
    risk: DELIVERY_RISK,
  },

  // -------------------------------------------------------------- ATR trend
  {
    id: 'volatility-trend-atr-intraday',
    name: 'Volatility Trend ATR — Intraday',
    category: 'Intraday', family: 'Volatility',
    indicators: ['ATR', 'SUPERTREND', 'EMA'],
    description:
      'The reference family enters when current ATR exceeds a past ATR (a ' +
      'volatility expansion) in the trend direction. The grammar compares an ' +
      'operand with another operand or a number, not with its own past, so ' +
      'expansion is expressed as ATR above a floor you set for the instrument, ' +
      'with Supertrend and EMA carrying direction.',
    defaults: 'Entry: ATR > 5 (edit for the instrument) AND close > Supertrend AND close > EMA 200. Exit: close crosses below Supertrend.',
    source: `${POOL_URL} · strategy_volatility_trend_atr_intraday`,
    entry: { combinator: 'AND', conditions: [
      c('ATR', '>', 5), c('CLOSE', '>', 'SUPERTREND'), c('CLOSE', '>', 'EMA'),
    ] },
    exit: { combinator: 'OR', conditions: [c('CLOSE', 'CROSSES_BELOW', 'SUPERTREND')] },
    risk: INTRADAY_RISK,
  },
  {
    id: 'volatility-trend-atr-delivery',
    name: 'Volatility Trend ATR — Delivery',
    category: 'Delivery', family: 'Volatility',
    indicators: ['ATR', 'SUPERTREND', 'EMA'],
    description: 'The volatility-expansion trend entry held across sessions.',
    defaults: 'Entry: ATR > 5 (edit) AND close > Supertrend AND close > EMA 200. Exit: close crosses below Supertrend. Stop 4%, target 10%.',
    source: `${POOL_URL} · strategy_volatility_trend_atr_delivery`,
    entry: { combinator: 'AND', conditions: [
      c('ATR', '>', 5), c('CLOSE', '>', 'SUPERTREND'), c('CLOSE', '>', 'EMA'),
    ] },
    exit: { combinator: 'OR', conditions: [c('CLOSE', 'CROSSES_BELOW', 'SUPERTREND')] },
    risk: DELIVERY_RISK,
  },
]

export const POOL_FAMILIES: PoolFamily[] = ['Trend', 'Momentum', 'Mean reversion', 'Volatility']

/** Case-insensitive search across name, family, indicators and source. */
export function searchPool(query: string, family: PoolFamily | '', category: PoolCategory | ''): PoolTemplate[] {
  const q = query.trim().toLowerCase()
  return STRATEGY_POOL.filter((t) =>
    (!family || t.family === family) &&
    (!category || t.category === category) &&
    (!q || [t.name, t.family, t.category, t.source, ...t.indicators]
      .join(' ').toLowerCase().includes(q)))
}
